<template>
  <!-- Pour tester cette page, lancer en debug mocha sur le serveur de notification, et récupérer l'url générée (services/pdfCreator.js)-->
  <b-container fluid class="">
    <!-- h1 tag mandatory : id "export-done" is used to notify puppeteer when the export is done. -->
    <h1 v-if="exportDone" id="export-done" class="m-3">
      {{ $t("global.exportDone") }}
    </h1>
    <h1 v-else class="m-3">
      {{ $t("global.exportInProgress") }}
    </h1>
    <table-renderer
      v-for="data of gamesTablesData"
      :key="gamesTableIdPrefix + data.tableNumber"
      class="export"
      :table-number="data.tableNumber"
      :table-id-prefix="gamesTableIdPrefix"
      :data-table="data.dataTable"
      :dimension-fields="gamesDimensionColumnsTables"
      :measure-fields="gamesMeasureColumnsTables"
      :table-header="data.tableHeader"
      :group-headers="gamesGroupHeaders"
      :default-sorted-column="gamesDefaultSortedColumnTables"
      :totals-data="data.totalsData"
      :data-ready="gamesDataReadyTables"
      :hide-groups="gamesHideGroupsByTableNumber[data.tableNumber] === true"
      custom-table-class="table-width-xl"
    />
    <table-renderer
      v-for="data of kioskGlobalTablesData"
      :key="kioskGlobalTableIdPrefix + data.tableNumber"
      class="export"
      :table-number="data.tableNumber"
      :table-id-prefix="kioskGlobalTableIdPrefix"
      :data-table="data.dataTable"
      :dimension-fields="kioskGlobalDimensionColumnsTables"
      :measure-fields="kioskGlobalMeasureColumnsTables"
      :table-header="data.tableHeader"
      :default-sorted-column="kioskGlobalDefaultSortedColumnTables"
      :totals-data="data.totalsData"
      :data-ready="kioskGlobalDataReadyTables"
      custom-table-class="table-width-medium"
    />
    <table-renderer
      v-for="data of staffTablesData"
      :key="staffTableIdPrefix + data.tableNumber"
      class="export"
      :table-number="data.tableNumber"
      :table-id-prefix="staffTableIdPrefix"
      :data-table="data.dataTable"
      :dimension-fields="staffDimensionColumnsTables"
      :measure-fields="staffMeasureColumnsTables"
      :table-header="data.tableHeader"
      :group-headers="staffGroupHeaders"
      :default-sorted-column="staffDefaultSortedColumnTables"
      :totals-data="data.totalsData"
      :data-ready="staffDataReadyTables"
      custom-table-class="table-width-medium"
    />
  </b-container>
</template>

<script>
import { mapState } from "vuex";
import TableRenderer from "@/components/TableRenderer.vue";
import dimensionEnum from "@/model/dimensionEnum";
import { dateFormatEnum } from "@/filter/dateFormat";
import { ExportMail } from "@/router/routeNames";
import TableTools from "@/utils/tableTools";
import Tools from "@/utils/tools";
import { exportHelper } from "@/utils/exportHelper";
import moment from "moment";

export default {
  name: ExportMail,
  components: {
    TableRenderer,
  },
  data() {
    return {
      /** Tableau de filtres tels qu'attendu par cubeJs.
       * Ex :
       *    [
       *      {
       *        dimension: "Site.Id",
       *        operator: "equals",
       *        values: ["85007419-23D3-48AB-89E7-68089F46FA7D"]
       *      },
       *      {
       *        dimension: "BusinessDate.id",
       *        operator: "inDateRange",
       *        values: ["2020-09-23", "2020-09-26"]
       *      },
       */
      commonFilters: [],
      exportDone: false,
    };
  },
  computed: {
    ...mapState({
      gamesDimensionColumnsTables: (state) =>
        state.games.dimensionColumnsTables,
      gamesMeasureColumnsTables: (state) => state.games.measureColumnsTables,
      gamesGroupHeaders: (state) => state.games.groupHeaders,
      gamesDefaultSortedColumnTables: (state) =>
        state.games.defaultSortedColumnTables,
      gamesTablesData: (state) => state.games.tablesData,
      gamesHideGroupsByTableNumber: (state) =>
        state.games.hideGroupsByTableNumber,
      gamesDataReadyTables: (state) => state.games.dataReadyTables,
      kioskGlobalDimensionColumnsTables: (state) =>
        state.kioskGlobal.dimensionColumnsTables,
      kioskGlobalMeasureColumnsTables: (state) =>
        state.kioskGlobal.measureColumnsTables,
      kioskGlobalDefaultSortedColumnTables: (state) =>
        state.kioskGlobal.defaultSortedColumnTables,
      kioskGlobalTablesData: (state) => state.kioskGlobal.tablesData,
      kioskGlobalDataReadyTables: (state) => state.kioskGlobal.dataReadyTables,
      staffDimensionColumnsTables: (state) =>
        state.staff.dimensionColumnsTables,
      staffMeasureColumnsTables: (state) => state.staff.measureColumnsTables,
      staffGroupHeaders: (state) => state.staff.groupHeaders,
      staffDefaultSortedColumnTables: (state) =>
        state.staff.defaultSortedColumnTables,
      staffTablesData: (state) => state.staff.tablesData,
      staffDataReadyTables: (state) => state.staff.dataReadyTables,
    }),
    allDataReadyTables() {
      return (
        this.gamesDataReadyTables &&
        this.kioskGlobalDataReadyTables &&
        this.staffDataReadyTables
      );
    },
    gamesTableIdPrefix() {
      return "games";
    },
    kioskGlobalTableIdPrefix() {
      return "kiosk-global";
    },
    staffTableIdPrefix() {
      return "staff";
    },
  },
  watch: {},
  mounted: async function () {
    try {
      this.resetAllDataReadyTables();
      this.initAllDisplayTables();

      const queryFilters =
        this.$route.query.filter != null
          ? JSON.parse(decodeURIComponent(this.$route.query.filter))
          : null;

      if (queryFilters != null) {
        const siteFilter = {
          dimension: dimensionEnum.SITE,
          operator: "equals",
          values: queryFilters.siteIds,
        };
        const momentStartDate = moment(
          queryFilters.dateRange[0],
          dateFormatEnum.SQL_DATE_FORMAT
        );
        const momentEndDate =
          queryFilters.dateRange.length > 1
            ? moment(queryFilters.dateRange[1], dateFormatEnum.SQL_DATE_FORMAT)
            : momentStartDate;

        if (momentStartDate.isValid() && momentEndDate.isValid()) {
          this.commonFilters = [
            siteFilter,
            {
              dimension: dimensionEnum.DATE,
              operator: "inDateRange",
              values: queryFilters.dateRange,
            },
          ];
          const siteValues = await this.getSites([siteFilter]);

          await this.updateAllTableLists(siteValues);

          await this.createPdf(momentStartDate, momentEndDate, siteValues);
        } else {
          console.error(
            `Invalid date range received in ExportMail page : ${queryFilters.dateRange}.`
          );
        }
      } else {
        console.error("No filter have been received in ExportMail page.");
      }
    } catch (e) {
      console.error(e);
      console.error("Unexpected exception occured : " + e.message);
    }
  },
  methods: {
    async getSites(siteFilter) {
      const query = {
        dimensions: [dimensionEnum.SITE, dimensionEnum.SITE_NAME],
        filters: siteFilter,
      };
      const resultSet = await TableTools.Helper.doRequest(query);
      return resultSet.map((o) => {
        return {
          id: o[dimensionEnum.SITE],
          label: o[dimensionEnum.SITE_NAME],
        };
      });
    },
    resetAllDataReadyTables() {
      this.$store.dispatch("games/resetDataReadyTables");
      this.$store.dispatch("kioskGlobal/resetDataReadyTables");
      this.$store.dispatch("staff/resetDataReadyTables");
    },
    initAllDisplayTables() {
      this.$store.dispatch("games/initDisplayTables");
      this.$store.dispatch("kioskGlobal/initDisplayTables");
      this.$store.dispatch("staff/initDisplayTables");
    },
    async updateAllTableLists(siteValues) {
      await this.$store.dispatch("games/updateTableList", {
        filters: this.commonFilters,
        siteValues: siteValues,
        withCategoriesRows: false,
      });
      await this.$store.dispatch("kioskGlobal/updateTableList", {
        filters: this.commonFilters,
        siteValues: siteValues,
      });
      await this.$store.dispatch("staff/updateTableList", {
        filters: this.commonFilters,
        siteValues: siteValues,
      });
    },
    async createPdf(momentStartDate, momentEndDate, siteValues) {
      const self = this;
      if (self.allDataReadyTables) {
        const filename = `Rapports_${momentStartDate.format(
          dateFormatEnum.DATE_FORMAT_FOR_FILENAME
        )}_${momentEndDate.format(dateFormatEnum.DATE_FORMAT_FOR_FILENAME)}`;
        const title = self.$t("export.mailexporttitle");
        const labelDatePeriod = Tools.BusinessDateHelper.getLabelPeriod(
          momentStartDate,
          momentEndDate,
          self.$t("filter.period_default_prefix")
        );
        const subtitles = Tools.StringHelper.constructExportSubtitles(
          siteValues.map((v) => v.label),
          labelDatePeriod
        );

        let doc = exportHelper.createPdfDoc(
          null,
          [
            {
              idPrefix: self.gamesTableIdPrefix,
              groupTitle: self.$t("title.games"),
              tableNumbers: self.gamesTablesData.map((d) => d.tableNumber),
            },
            {
              idPrefix: self.kioskGlobalTableIdPrefix,
              groupTitle: self.$t("title.kiosk_global"),
              tableNumbers: self.kioskGlobalTablesData.map(
                (d) => d.tableNumber
              ),
            },
            {
              idPrefix: self.staffTableIdPrefix,
              groupTitle: self.$t("title.staff"),
              tableNumbers: self.staffTablesData.map((d) => d.tableNumber),
            },
          ],
          { title, subtitles, orientationPaysage: true, reduceHeader: false }
        );

        if (self.$route.query.outputmode == "inmemory") {
          window.pdfdata = doc.output("datauristring", {
            filename: `${filename}.pdf`,
          });
          self.exportDone = true;
        } else {
          var pdfPromise = await doc.save(`${filename}.pdf`, {
            returnPromise: true,
          });
          pdfPromise.onwriteend = function () {
            setTimeout(() => {
              self.exportDone = true;
            }, 500);
          };
        }
      } else {
        console.error("Data is not ready for export");
      }
    },
  },
};
</script>
